import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { domainsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Mail, Lock, Sun, Moon, Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [appConfig, setAppConfig] = useState({ app_name: 'ProMail', logo_url: '', primary_color: '#0066DA' });
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    loadAppConfig();
    loadDomains();
  }, []);

  const loadAppConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/config/public`);
      setAppConfig(response.data);
    } catch (error) {
      console.error('Failed to load app config');
    }
  };

  const loadDomains = async () => {
    try {
      const response = await domainsAPI.getPublicDomains();
      setDomains(response.data.domains || []);
      if (response.data.domains?.length === 1) {
        setSelectedDomain(response.data.domains[0].domain);
      }
    } catch (error) {
      console.error('Failed to load domains');
    }
  };

  const getFullEmail = () => {
    if (!username) return '';
    // If user already typed a full email, use it directly
    if (username.includes('@')) return username;
    // Otherwise append selected domain
    if (selectedDomain) return `${username}${selectedDomain}`;
    return username;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = getFullEmail();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (!email.includes('@')) {
      toast.error('Veuillez sélectionner un domaine ou entrer une adresse email complète');
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Connexion réussie !');
      navigate('/email');
    } catch (error) {
      const message = error.response?.data?.detail || 'Identifiants invalides';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1557200134-90327ee9fafa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBlbWFpbHxlbnwwfHx8fDE3NDAwMDAwMDB8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            {appConfig.logo_url ? (
              <div className="w-14 h-14 rounded-lg bg-white overflow-hidden">
                <img src={appConfig.logo_url} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: appConfig.primary_color }}>
                <Mail className="w-8 h-8 text-white" />
              </div>
            )}
            <h1 className="text-4xl font-bold font-['Plus_Jakarta_Sans']">{appConfig.app_name}</h1>
          </div>
          <p className="text-xl text-center text-slate-200 max-w-md">
            Votre messagerie professionnelle
          </p>
          <div className="mt-12 grid grid-cols-2 gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span>Synchronisation automatique</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span>Accès hors connexion</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span>Calendrier intégré</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span>Multi-domaines</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Theme toggle */}
          <div className="flex justify-end mb-6">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          {/* Mobile logo */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-8">
            {appConfig.logo_url ? (
              <div className="w-12 h-12 rounded-lg bg-white overflow-hidden">
                <img src={appConfig.logo_url} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: appConfig.primary_color }}>
                <Mail className="w-6 h-6 text-white" />
              </div>
            )}
            <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{appConfig.app_name}</h1>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center font-['Plus_Jakarta_Sans']">
                Connexion
              </CardTitle>
              <CardDescription className="text-center">
                Utilisez vos identifiants email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Domain selector (only shown if domains exist) */}
                {domains.length > 0 && (
                  <div className="space-y-2">
                    <Label>Domaine</Label>
                    <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                      <SelectTrigger data-testid="domain-selector">
                        <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Sélectionnez votre domaine" />
                      </SelectTrigger>
                      <SelectContent>
                        {domains.map((d) => (
                          <SelectItem key={d.id} value={d.domain}>
                            {d.display_name} ({d.domain})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="login-email">
                    {domains.length > 0 && selectedDomain ? "Nom d'utilisateur" : 'Adresse email'}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type={domains.length > 0 && selectedDomain ? 'text' : 'email'}
                      placeholder={domains.length > 0 && selectedDomain ? `utilisateur` : 'votre@email.com'}
                      className="pl-10"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={loading}
                      data-testid="login-email-input"
                    />
                    {selectedDomain && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        {selectedDomain}
                      </span>
                    )}
                  </div>
                  {selectedDomain && username && (
                    <p className="text-xs text-muted-foreground pl-1">
                      {getFullEmail()}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="********"
                      className="pl-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-hover" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground text-center">
                  {domains.length > 0
                    ? "Sélectionnez votre domaine et entrez vos identifiants."
                    : "Utilisez les identifiants de votre compte email."
                  }
                  <br />
                  Votre session restera active même hors connexion.
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Problème de connexion ? Contactez votre administrateur
          </p>
        </div>
      </div>
    </div>
  );
}
